import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, collection, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import StudentDashboard from "./pages/StudentDashboard";
import StudentCourse from "./pages/StudentCourse";
import LandingPage from "./pages/LandingPage";
import LoadingScreen from "./components/LoadingScreen";
import MockInterview from "./pages/MockInterview";

import { auth, db, functions } from "./firebase-config";
import { BUILD_VERSION } from "./buildVersion";

const ADMIN_UID = "62Dj0eIHKJW4sHlVLi62wvvpQHH3";
const ADMIN_ANALYTICS_LIMIT = 3000;

const APP_VERSION_CHECK_INTERVAL_MS = 2 * 60 * 1000;
const APP_VERSION_INITIAL_CHECK_DELAY_MS = 15 * 1000;

const getOrCreateSessionId = (uid) => {
  const key = `va_session_${uid}`;
  let sessionId = localStorage.getItem(key);

  if (!sessionId) {
    sessionId = `${uid}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
    localStorage.setItem(key, sessionId);
  }

  return sessionId;
};

function AppRoutes({ currentUser, isAdmin, loading, sharedProps }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !currentUser) return;

    const path = window.location.pathname;

    if (isAdmin && !path.startsWith("/admin")) {
      navigate("/admin/dashboard", { replace: true });
    }

    if (
      !isAdmin &&
      !path.startsWith("/student/dashboard") &&
      !path.startsWith("/student/course") &&
      !path.startsWith("/student/mock-interview")
    ) {
      navigate("/student/dashboard", { replace: true });
    }
  }, [currentUser, isAdmin, loading, navigate]);

  if (loading) {
    return <LoadingScreen message="Verifying access..." />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/admin/dashboard"
        element={
          currentUser && isAdmin ? (
            <AdminDashboard {...sharedProps} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/admin/student-preview"
        element={
          currentUser && isAdmin ? (
            <StudentDashboard
              {...sharedProps}
              adminPreview
              studentProfile={{
                authorizedCourses: sharedProps.courses.map((course) => course.id),
                hasAiAccess: true,
                enabled: true,
                role: "admin-preview",
              }}
            />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/admin/student-preview/course/:courseId"
        element={
          currentUser && isAdmin ? (
            <StudentCourse
              courses={sharedProps.courses}
              adminPreview
            />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/admin/student-preview/mock-interview"
        element={
          currentUser && isAdmin ? (
            <MockInterview adminPreview />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/student/dashboard"
        element={
          currentUser && !isAdmin ? (
            <StudentDashboard {...sharedProps} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/student/mock-interview"
        element={
          currentUser && !isAdmin ? (
            <MockInterview />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/student/course/:courseId"
        element={
          currentUser && !isAdmin ? (
            <StudentCourse courses={sharedProps.courses} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [studentProfile, setStudentProfile] = useState(null);

  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [videoLogs, setVideoLogs] = useState([]);
  const [videoSessions, setVideoSessions] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [adminAnalyticsLoading, setAdminAnalyticsLoading] = useState(false);
  const [adminAnalyticsError, setAdminAnalyticsError] = useState("");
  const [adminAnalyticsLastLoadedAt, setAdminAnalyticsLastLoadedAt] = useState(null);

  useEffect(() => {
    let unsubProfile = null;
    let currentSessionId = null;

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (unsubProfile) unsubProfile();

      if (!user) {
        setCurrentUser(null);
        setIsAdmin(false);
        setStudentProfile(null);
        setUsers([]);
        setCourses([]);
        setVideoLogs([]);
        setVideoSessions([]);
        setLoginLogs([]);
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      if (user.uid === ADMIN_UID) {
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      currentSessionId = getOrCreateSessionId(user.uid);

      try {
        const startStudentSession = httpsCallable(
          functions,
          "startStudentSession"
        );

        await startStudentSession({
          sessionId: currentSessionId,
        });
      } catch (err) {
        console.error("Student session start failed:", err);
      }

      unsubProfile = onSnapshot(
        doc(db, "users", user.uid),
        async (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setStudentProfile(data);

            if (
              data.activeSessionId &&
              currentSessionId &&
              data.activeSessionId !== currentSessionId
            ) {
              alert(
                "Your account was logged in on another device. You will be logged out here."
              );

              await signOut(auth);
              return;
            }
          } else {
            setStudentProfile({ authorizedCourses: [] });
          }

          setIsAdmin(false);
          setLoading(false);
        },
        (err) => {
          console.error("Profile sync error:", err);
          setLoading(false);
        }
      );
    });

    return () => {
      unsubAuth();
      if (unsubProfile) unsubProfile();
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const unsubCourses = onSnapshot(
      collection(db, "courses"),
      (snap) => {
        setCourses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error("Courses sync error:", err);
      }
    );

    return () => unsubCourses();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !isAdmin) return;

    const unsubUsers = onSnapshot(
      collection(db, "users"),
      (snap) => {
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error("Users sync error:", err);
      }
    );

    return () => unsubUsers();
  }, [currentUser, isAdmin]);

  // ✅ Manual-only admin analytics.
  // These three Cloud Functions run only when the admin clicks Refresh Analytics Data.
  const refreshAdminAnalyticsData = async () => {
    if (!currentUser || !isAdmin || adminAnalyticsLoading) return;

    try {
      setAdminAnalyticsLoading(true);
      setAdminAnalyticsError("");

      const getVideoLogs = httpsCallable(functions, "getVideoLogs");
      const getLoginLogs = httpsCallable(functions, "getLoginLogs");
      const getVideoSessions = httpsCallable(functions, "getVideoSessions");

      const [videoLogsResult, loginLogsResult, videoSessionsResult] =
        await Promise.all([
          getVideoLogs({ limit: ADMIN_ANALYTICS_LIMIT }),
          getLoginLogs({ limit: ADMIN_ANALYTICS_LIMIT }),
          getVideoSessions({ limit: ADMIN_ANALYTICS_LIMIT }),
        ]);

      const nextVideoLogs = Array.isArray(videoLogsResult?.data?.logs)
        ? videoLogsResult.data.logs
        : [];

      const nextLoginLogs = Array.isArray(loginLogsResult?.data?.logs)
        ? loginLogsResult.data.logs
        : [];

      const nextVideoSessions = Array.isArray(videoSessionsResult?.data?.sessions)
        ? videoSessionsResult.data.sessions
        : [];

      nextVideoSessions.sort(
        (a, b) => (b.endedAtMs || 0) - (a.endedAtMs || 0)
      );

      setVideoLogs(nextVideoLogs);
      setLoginLogs(nextLoginLogs);
      setVideoSessions(nextVideoSessions);
      setAdminAnalyticsLastLoadedAt(Date.now());
    } catch (err) {
      console.error("Manual admin analytics refresh failed:", err);
      setAdminAnalyticsError(
        err?.message || "Failed to refresh admin analytics data."
      );
    } finally {
      setAdminAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const checkForAppUpdate = async () => {
      if (document.visibilityState === "hidden") return;

      try {
        const response = await fetch(`/version.json?ts=${Date.now()}`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });

        if (!response.ok) return;

        const latest = await response.json();
        const latestVersion = String(latest?.version || "").trim();

        if (
          !cancelled &&
          latestVersion &&
          latestVersion !== BUILD_VERSION
        ) {
          setUpdateAvailable(true);
        }
      } catch (error) {
        console.warn("App version check failed:", error);
      }
    };

    const initialTimer = setTimeout(
      checkForAppUpdate,
      APP_VERSION_INITIAL_CHECK_DELAY_MS
    );

    const interval = setInterval(
      checkForAppUpdate,
      APP_VERSION_CHECK_INTERVAL_MS
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForAppUpdate();
      }
    };

    window.addEventListener("focus", checkForAppUpdate);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      clearInterval(interval);
      window.removeEventListener("focus", checkForAppUpdate);
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, []);

  const refreshToLatestVersion = () => {
    window.location.reload();
  };

  const sharedProps = {
    users,
    courses,
    videoLogs,
    videoSessions,
    loginLogs,
    studentProfile,
    currentUserId: currentUser?.uid || null,
    ADMIN_UID,
    refreshAdminAnalyticsData,
    adminAnalyticsLoading,
    adminAnalyticsError,
    adminAnalyticsLastLoadedAt,
  };

  return (
    <div className="min-h-screen w-full bg-gray-900 text-white">
      <Router>
        <AppRoutes
          currentUser={currentUser}
          isAdmin={isAdmin}
          loading={loading}
          sharedProps={sharedProps}
        />
      </Router>

      {updateAvailable && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-orange-500/40 bg-gray-900 p-6 text-white shadow-2xl">
            <div className="mb-4 text-4xl">🚀</div>

            <h2 className="text-2xl font-bold text-orange-400">
              New Version Available
            </h2>

            <p className="mt-3 leading-relaxed text-gray-300">
              VA Academy Pro has been updated. Refresh now to use the latest video player and improvements.
            </p>

            <p className="mt-3 text-sm text-gray-400">
              Your saved lesson progress will remain available after the refresh.
            </p>

            <button
              type="button"
              onClick={refreshToLatestVersion}
              className="mt-6 w-full rounded-xl bg-orange-500 px-5 py-3 font-semibold text-white transition hover:bg-orange-600"
            >
              Refresh Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}